package org.example.project.engineletter.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.enginetabular.currentDateDE
import org.example.project.service.LoginService
import org.example.project.service.LoginUserApi
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

@Composable
fun WEPalettenscheinLayout(
    rootElementId: String? = null,
    auftrag: All,
    unternehmen: All,
    logoSrc: String,
    ausstellerSignatur: String = "",
    ausstellerName: String = "",
    fahrerSignatur: String = "",
    fahrerName: String = ""
) {
    SupportLayoutSysCustomDocumentContainer(rootElementId = rootElementId) {
        WEPalettenscheinPage {
            WEPalettenscheinHeader(
                unternehmen = unternehmen,
                logoSrc = logoSrc
            )
            WEPalettenscheinBody(auftrag, unternehmen, currentDateDE(), ausstellerSignatur, ausstellerName, fahrerSignatur, fahrerName)
            WEFooterNote()
        }
    }
}

@Composable
private fun WEPalettenscheinPage(content: @Composable () -> Unit) {
    Div({
        attr("data-letter-pdf-page", "true")
        attr("data-letter-page-number", "1")
        style {
            width(794.px)
            height(1123.px)
            backgroundColor(Color.white)
            color(Color("#111111"))
            borderRadius(10.px)
            border(1.px, LineStyle.Solid, Color("#d7dbe3"))
            property("box-shadow", "0 30px 70px rgba(0,0,0,0.22)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("box-sizing", "border-box")
            property("padding-left", "18mm")
            property("padding-right", "18mm")
            property("padding-top", "42px")
            property("padding-bottom", "48px")
            property("overflow", "hidden")
        }
    }) {
        content()
    }
}

@Composable
private fun WEPalettenscheinHeader(unternehmen: All, logoSrc: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            textAlign("center")
        }
    }) {
        if (logoSrc.isNotBlank()) {
            Img(src = logoSrc, attrs = {
                style {
                    property("max-width", "360px")
                    property("max-height", "150px")
                    property("object-fit", "contain")
                    marginBottom(10.px)
                }
            })
        }
        Div({
            style {
                fontSize(15.px)
                lineHeight(20.px)
                marginBottom(16.px)
            }
        }) {
            Text(weCompanyHeaderLine(unternehmen))
        }
        Div({
            style {
                width(100.percent)
                height(1.px)
                property("background", "#111111")
                marginBottom(18.px)
            }
        })
    }
}

@Composable
private fun WEPalettenscheinBody(auftrag: All, unternehmen: All, datum: String, ausstellerSignatur: String, ausstellerName: String, fahrerSignatur: String, fahrerName: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            fontSize(14.px)
            lineHeight(19.px)
            gap(28.px)
            flexGrow(1)
        }
    }) {
        WETitleMetaRow(auftrag = auftrag, datum = datum)
        WEPartyCards(auftrag)
        WEVehicleStrip(auftrag)
        WEPalletMatrix(auftrag)
        WESignatureBlock(auftrag, ausstellerSignatur, ausstellerName, fahrerSignatur, fahrerName)
    }
}

@Composable
private fun WETitleMetaRow(auftrag: All, datum: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            marginTop(55.px)
            marginBottom(27.px)
            textAlign("center")
        }
    }) {
        Div({ style { fontSize(30.px); lineHeight(36.px); property("font-weight", "700") } }) {
            Text("Palettenbelegschein Wareneingang")
        }
        WEHeaderMetaRow(auftrag = auftrag, datum = datum)
    }
}

@Composable
private fun SpanBold(value: String) {
    org.jetbrains.compose.web.dom.Span({ style { property("font-weight", "700") } }) { Text(value) }
}

@Composable
private fun WEHeaderMetaRow(auftrag: All, datum: String) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(3, 1fr)")
            gap(18.px)
            width(100.percent)
            fontSize(16.px)
            lineHeight(20.px)
        }
    }) {
        WEMetaItem("Lieferschein", auftrag.lieferscheinnummer.ifBlank { "-" })
        WEMetaItem("Datum", datum.ifBlank { "-" })
        WEMetaItem("Uhrzeit", weCurrentTimeDE())
    }
}

@Composable
private fun WEMetaItem(label: String, value: String) {
    Div({
        style {
            padding(8.px, 0.px)
            textAlign("center")
        }
    }) {
        Text("$label: ")
        SpanBold(value)
    }
}

@Composable
private fun WEPartyCards(auftrag: All) {
    val items = listOf(
        "Empfänger" to weAddressLines(parseWEAddress(auftrag.empfaenger)),
        "Lieferant" to weAddressLines(parseWEAddress(auftrag.lieferant)),
        "Auftraggeber" to weAddressLines(parseWEAddress(auftrag.auftraggeber)),
        "Unternehmer" to weAddressLines(parseWEAddress(auftrag.unternehmer))
    )
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1fr 1fr 1fr 1fr")
            border(1.px, LineStyle.Solid, Color("#111111"))
        }
    }) {
        items.forEachIndexed { index, item ->
            Div({
                style {
                    padding(34.px, 14.px, 24.px, 14.px)
                    property("box-sizing", "border-box")
                    if (index < items.lastIndex) property("border-right", "1px solid #111111")
                    minHeight(142.px)
                }
            }) {
                Div({ style { fontSize(15.px); lineHeight(20.px); marginBottom(16.px); property("font-weight", "700") } }) { Text(item.first) }
                item.second.forEachIndexed { lineIndex, line ->
                    Div({
                        style {
                            fontSize(if (lineIndex == 0) 15.px else 13.px)
                            lineHeight(if (lineIndex == 0) 20.px else 18.px)
                            property("word-break", "break-word")
                        }
                    }) { Text(line) }
                }
            }
        }
    }
}

@Composable
private fun WEVehicleStrip(auftrag: All) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1.2fr 1fr 1.25fr")
            border(1.px, LineStyle.Solid, Color("#111111"))
            minHeight(56.px)
            alignItems(AlignItems.Center)
            marginTop(28.px)
            marginBottom(28.px)
        }
    }) {
        WEVehicleCell("images/user.svg", "Fahrer", auftrag.fahrername.ifBlank { auftrag.name })
        WEVehicleCell("images/lkw.svg", "LKW", auftrag.lkwkennzeichen)
        WEVehicleCell("images/auflieger-dunkel.svg", "Auflieger", auftrag.aufliegerkennzeichen, last = true)
    }
}

@Composable
private fun WEVehicleCell(icon: String, label: String, value: String, last: Boolean = false) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(8.px)
            padding(12.px, 14.px)
            if (!last) property("border-right", "1px solid #111111")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(8.px)
                property("font-weight", "700")
                property("white-space", "nowrap")
            }
        }) {
            Img(src = icon, attrs = {
                style {
                    width(24.px)
                    height(24.px)
                    property("filter", "brightness(0)")
                }
            })
            Text(label)
        }
        Div({
            style {
                fontSize(16.px)
                lineHeight(20.px)
                textAlign("center")
                property("word-break", "break-word")
            }
        }) {
            Text(value.ifBlank { "-" })
        }
    }
}

@Composable
private fun WEPalletMatrix(auftrag: All) {
    val values = wePalletRow(auftrag)
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1fr 0.68fr 1fr")
            border(1.px, LineStyle.Solid, Color("#111111"))
        }
    }) {
        WEPalletHeader("EINGANG")
        WEPalletHeader("PALETTENART", middle = true)
        WEPalletHeader("AUSGANG")
        WEPalletValue(values.getOrNull(0).orEmpty())
        WEPalletType(wePalletType(auftrag))
        WEPalletValue(values.getOrNull(1).orEmpty())
    }
}

@Composable
private fun WEPalletHeader(label: String, middle: Boolean = false) {
    Div({
        style {
            padding(16.px)
            textAlign("center")
            fontSize(18.px)
            lineHeight(22.px)
            property("font-weight", "700")
            if (middle) {
                property("border-left", "1px solid #111111")
                property("border-right", "1px solid #111111")
            }
        }
    }) { Text(label) }
}

@Composable
private fun WEPalletValue(value: String) {
    Div({
        style {
            minHeight(56.px)
            property("border-top", "1px solid #111111")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("justify-content", "center")
            fontSize(26.px)
            lineHeight(32.px)
            property("font-weight", "700")
        }
    }) { Text(value.ifBlank { "-" }) }
}

@Composable
private fun WEPalletType(value: String) {
    Div({
        style {
            minHeight(56.px)
            property("border-top", "1px solid #111111")
            property("border-left", "1px solid #111111")
            property("border-right", "1px solid #111111")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("justify-content", "center")
            textAlign("center")
            fontSize(18.px)
            lineHeight(22.px)
            property("font-weight", "700")
            padding(0.px, 8.px)
            property("box-sizing", "border-box")
        }
    }) { Text(value.ifBlank { "-" }) }
}

@Composable
private fun WEDocumentMeta(auftrag: All, datum: String) {
    WETwoColumnGrid {
        WEReceiptField("Lieferschein", auftrag.lieferscheinnummer)
        WEReceiptField("Datum", datum)
    }
}

@Composable
private fun WEPartyGrid(auftrag: All) {
    WETwoColumnGrid {
        WEReceiptField("Auftraggeber", companyName(auftrag.auftraggeber))
        WEReceiptField("Unternehmer", companyName(auftrag.unternehmer))
        WEReceiptField("Empfänger", companyName(auftrag.empfaenger))
        WEReceiptField("Lieferant", companyName(auftrag.lieferant))
    }
}

@Composable
private fun WEVehicleGrid(auftrag: All) {
    WEThreeColumnGrid {
        WEReceiptField("Fahrer", auftrag.fahrername.ifBlank { auftrag.name })
        WEReceiptField("LKW", auftrag.lkwkennzeichen)
        WEReceiptField("Auflieger", auftrag.aufliegerkennzeichen)
    }
}

@Composable
private fun WEPalletMetrics(auftrag: All) {
    val paletten = wePalletRow(auftrag)
    WETwoColumnGrid {
        WEReceiptMetric("Eingang", paletten.getOrNull(0).orEmpty())
        WEReceiptMetric("Ausgang", paletten.getOrNull(1).orEmpty())
    }
}

@Composable
private fun WETwoColumnGrid(content: @Composable () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1fr 1fr")
            gap(18.px, 22.px)
            width(100.percent)
        }
    }) {
        content()
    }
}

@Composable
private fun WEThreeColumnGrid(content: @Composable () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1fr 1fr 1fr")
            gap(18.px)
            width(100.percent)
        }
    }) {
        content()
    }
}

@Composable
private fun WEReceiptField(label: String, value: String) {
    Div({
        style {
            padding(10.px, 0.px)
            property("border-bottom", "1px solid #111111")
        }
    }) {
        Div({ style { fontSize(12.px); lineHeight(16.px); property("font-weight", "700") } }) {
            Text("$label:")
        }
        Div({ style { fontSize(16.px); lineHeight(22.px); marginTop(4.px) } }) {
            Text(value.ifBlank { "-" })
        }
    }
}

@Composable
private fun WEReceiptMetric(label: String, value: String) {
    Div({
        style {
            padding(18.px, 0.px)
            property("border-bottom", "1px solid #111111")
            textAlign("center")
        }
    }) {
        Div({ style { fontSize(16.px); lineHeight(20.px); property("font-weight", "700") } }) {
            Text(label)
        }
        Div({ style { fontSize(34.px); lineHeight(42.px); marginTop(8.px); property("font-weight", "700") } }) {
            Text(value.ifBlank { "-" })
        }
    }
}

@Composable
private fun WEPalettenscheinRow(content: @Composable () -> Unit) {
    Div({ style { display(DisplayStyle.Flex); gap(18.px) } }) {
        content()
    }
}

@Composable
private fun WEBlock(title: String, lines: List<String>) {
    Div({
        style {
            width(50.percent)
            padding(22.px, 24.px)
            property("box-sizing", "border-box")
            border(1.px, LineStyle.Solid, Color("#111111"))
            lineHeight(20.px)
        }
    }) {
        Div({ style { property("font-weight", "700"); marginBottom(10.px) } }) {
            Text("$title:")
        }
        lines.forEach { line -> Div { Text(line.ifBlank { "-" }) } }
    }
}

@Composable
private fun WEBoldBlock(title: String, lines: List<String>) {
    Div({
        style {
            width(50.percent)
            padding(22.px, 24.px)
            property("box-sizing", "border-box")
            border(1.px, LineStyle.Solid, Color("#111111"))
            lineHeight(20.px)
            property("font-weight", "700")
        }
    }) {
        Div({ style { property("font-weight", "700"); marginBottom(10.px) } }) {
            Text("$title:")
        }
        lines.forEach { line -> Div { Text(line.ifBlank { "-" }) } }
    }
}

@Composable
private fun WEMetaLine(auftrag: All, datum: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(72.px)
            fontSize(15.px)
            lineHeight(20.px)
        }
    }) {
        Div { Text("Lieferschein: ${auftrag.lieferscheinnummer.ifBlank { "-" }}") }
        Div { Text("Datum: ${datum.ifBlank { "-" }}") }
    }
}

@Composable
private fun WEFahrerLine(auftrag: All) {
    WEGridLine(
        labels = listOf("Fahrer:", "Kennzeichen:", "Auflieger:"),
        values = listOf(
            auftrag.fahrername.ifBlank { auftrag.name }.ifBlank { "-" },
            auftrag.lkwkennzeichen.ifBlank { "-" },
            auftrag.aufliegerkennzeichen.ifBlank { "-" }
        )
    )
}

@Composable
private fun WEPalettenTable(auftrag: All) {
    WEGridLine(labels = listOf("Eingang", "Ausgang"), values = wePalletRow(auftrag), centered = true)
}

@Composable
private fun WEGridLine(labels: List<String>, values: List<String>, centered: Boolean = false) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px) } }) {
        Div({ style { display(DisplayStyle.Flex); property("font-weight", "700") } }) {
            labels.forEach { label ->
                Div({ style { width((100 / labels.size).percent); textAlign(if (centered) "center" else "left") } }) {
                    Text(label)
                }
            }
        }
        Div({ style { display(DisplayStyle.Flex) } }) {
            values.forEach { value ->
                Div({ style { width((100 / values.size).percent); textAlign(if (centered) "center" else "left") } }) {
                    Text(value.ifBlank { "-" })
                }
            }
        }
    }
}

@Composable
private fun WEPlainTable(headers: List<String>, rows: List<List<String>>) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            border(1.px, LineStyle.Solid, Color("#111111"))
        }
    }) {
        WEPlainRow(headers, strong = true, isFirst = true)
        rows.forEach { row -> WEPlainRow(row, strong = false, isFirst = false) }
    }
}

@Composable
private fun WEPlainRow(values: List<String>, strong: Boolean, isFirst: Boolean) {
    Div({
        style {
            display(DisplayStyle.Flex)
            if (!isFirst) property("border-top", "1px solid #111111")
            if (strong) property("font-weight", "700")
        }
    }) {
        values.forEachIndexed { index, value ->
            Div({
                style {
                    width((100 / values.size).percent)
                    padding(5.px, 7.px)
                    property("box-sizing", "border-box")
                    if (index < values.size - 1) property("border-right", "1px solid #111111")
                    property("word-break", "break-word")
                    textAlign("left")
                }
            }) {
                Text(value.ifBlank { "-" })
            }
        }
    }
}

@Composable
private fun WESignatureBlock(auftrag: All, snapshotAusstellerSignatur: String, snapshotAusstellerName: String, snapshotFahrerSignatur: String, snapshotFahrerName: String) {
    val aussteller = LoginService.getUser()
    val fahrerText = auftrag.fahrername.ifBlank { auftrag.name }
    val fahrerUserId = auftrag.slotDriverUserId
    val fahrerFirmenId = weFahrerFirmenId(auftrag)
    var fahrerUser by remember(fahrerFirmenId, fahrerUserId) { mutableStateOf<All?>(null) }
    val hasSnapshotFahrer = snapshotFahrerSignatur.isNotBlank() || snapshotFahrerName.isNotBlank()
    var fahrerUserGeladen by remember(fahrerFirmenId, fahrerUserId, hasSnapshotFahrer) { mutableStateOf(hasSnapshotFahrer || fahrerUserId.isBlank()) }
    LaunchedEffect(fahrerFirmenId, fahrerUserId, hasSnapshotFahrer) {
        fahrerUserGeladen = hasSnapshotFahrer || fahrerUserId.isBlank()
        fahrerUser = if (hasSnapshotFahrer) null else loadWEFahrerUser(fahrerFirmenId, fahrerUserId)
        fahrerUserGeladen = true
    }
    Div({
        attr("data-yard-driver-signature-ready", fahrerUserGeladen.toString())
        style {
            marginTop(24.px)
            display(DisplayStyle.Flex)
            gap(56.px)
        }
    }) {
        WESignatureCell(
            title = "Bestätigung Aussteller",
            signaturBildSrc = snapshotAusstellerSignatur.ifBlank { aussteller?.signatur.orEmpty() },
            nameText = snapshotAusstellerName.ifBlank { aussteller?.let { listOfNotBlank(it.vorname, it.nachname).joinToString(" ") }.orEmpty() }
        )
        WESignatureCell(
            title = "Bestätigung Fahrer",
            signaturBildSrc = snapshotFahrerSignatur.ifBlank { fahrerUser?.signatur.orEmpty() },
            nameText = snapshotFahrerName.ifBlank { fahrerUser?.let { weUserDisplayName(it) }.orEmpty() }.ifBlank { fahrerText }
        )
    }
}

@Composable
private fun WESignatureCell(title: String, signaturBildSrc: String, nameText: String) {
    Div({
        style {
            width(50.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(5.px)
            textAlign("center")
        }
    }) {
        Div({ style { fontSize(15.px); lineHeight(20.px); property("font-weight", "700"); marginBottom(16.px) } }) { Text(title) }
        Div({
            style {
                height(46.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("justify-content", "center")
            }
        }) {
            if (signaturBildSrc.startsWith("data:image/")) {
                Img(src = signaturBildSrc, attrs = {
                    style {
                        property("max-width", "160px")
                        property("max-height", "44px")
                        property("object-fit", "contain")
                    }
                })
            }
        }
        Div({ style { property("border-top", "1px solid #111111"); lineHeight(17.px) } }) {
            Div { Text(nameText.ifBlank { "-" }) }
        }
    }
}

@Composable
private fun WEFooterNote() {
    Div({
        style {
            paddingTop(14.px)
            property("border-top", "1px solid #111111")
            fontSize(12.px)
            lineHeight(17.px)
        }
    }) {
        Div { Text("Der Fahrer bestätigt hiermit die Richtigkeit der Angaben. Beanstandungen sind unverzüglich zu melden.") }
    }
}

private fun wePalletRow(auftrag: All): List<String> =
    listOf(displayWEInt(weEingangPaletten(auftrag)), displayWEInt(auftrag.leerpalettenanzahl))

private fun wePalletType(auftrag: All): String =
    auftrag.slotLhmTyp
        .ifBlank { auftrag.typ }
        .ifBlank { auftrag.lhmTypId }
        .ifBlank { auftrag.slotLhmTypId }

private fun weEingangPaletten(auftrag: All): Int =
    auftrag.yardabschlussIstPaletten.takeIf { it != 0 }
        ?: auftrag.mobilWePalettenUnbeschaedigt.takeIf { it != 0 }
            ?.plus(auftrag.mobilWePalettenBruch.takeIf { it != 0 } ?: 0)
        ?: auftrag.palettenanzahl

private fun displayWEInt(value: Int): String =
    if (value == 0) "-" else value.toString()

private fun parseWEAddress(value: String): All {
    val parts = value.split(",").map { it.trim() }.filter { it.isNotBlank() }
    val plzOrt = parts.getOrNull(2).orEmpty()
    val plz = plzOrt.substringBefore(" ").takeIf { it.any(Char::isDigit) }.orEmpty()
    val ort = plzOrt.substringAfter(" ", "").trim()
    return All(firma = parts.getOrNull(0).orEmpty(), strasse = parts.getOrNull(1).orEmpty(), plz = plz, ort = ort)
}

private fun weCompanyAddress(unternehmen: All): All =
    All(firma = unternehmen.firma, strasse = unternehmen.strasse, hausnummer = unternehmen.hausnummer, plz = unternehmen.plz, ort = unternehmen.ort)

private fun weAddressLines(address: All): List<String> =
    listOf(
        address.firma.ifBlank { "-" },
        listOf(address.strasse, address.hausnummer).filter { it.isNotBlank() }.joinToString(" ").ifBlank { "-" },
        listOf(address.plz, address.ort).filter { it.isNotBlank() }.joinToString(" ").ifBlank { "-" }
    )

private fun weCompanyHeaderLine(unternehmen: All): String =
    listOf(
        unternehmen.firma,
        listOf(unternehmen.strasse, unternehmen.hausnummer).filter { it.isNotBlank() }.joinToString(" "),
        listOf(unternehmen.plz, unternehmen.ort).filter { it.isNotBlank() }.joinToString(" ")
    ).filter { it.isNotBlank() }.joinToString(" | ").ifBlank { "-" }

private fun weCurrentTimeDE(): String {
    val date = kotlin.js.Date()
    return "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
}

private fun companyName(value: String): String =
    value.split(",").firstOrNull().orEmpty().trim().ifBlank { value.trim() }

private fun listOfNotBlank(vararg values: String): List<String> =
    values.map { it.trim() }.filter { it.isNotBlank() }

private suspend fun loadWEFahrerUser(firmenId: String, userId: String): All? {
    val normalizedUserId = userId.trim()
    if (normalizedUserId.isBlank()) return null
    return LoginUserApi.loadCompanyUser(firmenId.trim(), normalizedUserId)
        ?: LoginUserApi.loadUser(normalizedUserId)
}

private fun weFahrerFirmenId(auftrag: All): String =
    auftrag.anbieterFirmenId
        .ifBlank { auftrag.lieferantFirmenId }
        .ifBlank { auftrag.unternehmerFirmenId }
        .ifBlank { auftrag.auftraggeberFirmenId }
        .trim()

private fun weUserDisplayName(user: All): String =
    listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
        .ifBlank { user.name }
